/**
 * Feature 100 — public /partner-trial landing page.
 * Product-supplied copy (source of truth: docs/pre-specify/100-fp-trial-accounts_spec.md).
 * Marketing sections + trial-request form; no login required.
 */

import { Brain, ClipboardList, HeartHandshake, Network, Workflow } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Card } from '@/shared/components/ui/card';
import { TrialRequestForm } from '../components/TrialRequestForm';

const FEATURES = [
  {
    icon: ClipboardList,
    title: 'Longevity Planning',
    body: 'A proven framework that helps clients prepare for aging, caregiving, and future life transitions before a crisis occurs.',
  },
  {
    icon: Brain,
    title: 'AI Care Intelligence',
    body: "Personalized recommendations, trusted resources, and actionable next steps for your clients' care-related questions.",
  },
  {
    icon: HeartHandshake,
    title: 'Care Concierge',
    body: 'Expert guidance for complex family situations, providing support while keeping you at the center of the client relationship.',
  },
  {
    icon: Network,
    title: 'Vetted Provider Network',
    body: 'Access to a nationwide network of trusted professionals across 30+ aging and caregiving specialties.',
  },
  {
    icon: Workflow,
    title: 'Advisor Workflow',
    body: 'Meeting tools, conversation guides, educational resources, and client materials designed to fit seamlessly into your advisory process.',
  },
];

const scrollToForm = () =>
  document.getElementById('trial-request-form')?.scrollIntoView({ behavior: 'smooth' });

export function PartnerTrialPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="container mx-auto px-4 pt-16 pb-12 text-center max-w-4xl">
        <h1 className="text-4xl md:text-5xl text-gray-900 mb-3">
          Experience the Future of Longevity Planning
        </h1>
        <p className="text-xl text-[#026857] font-medium mb-6">
          The Care Intelligence Platform for Financial Advisors
        </p>
        <p className="text-gray-600 mb-4">
          Today's clients expect more than investment advice. They look to their financial advisor
          for guidance through aging, caregiving, cognitive decline, healthcare decisions,
          end-of-life planning, and life's unexpected transitions.
        </p>
        <p className="text-gray-600 mb-8">
          bQuest gives you the technology, expert support, and trusted resources to confidently
          lead these conversations—helping you deepen client relationships, strengthen
          multigenerational connections, and protect the financial plans you've worked so hard to
          build. Request your complimentary 7-day Advisor Trial and experience the future of
          longevity planning.
        </p>
        <Button onClick={scrollToForm} className="bg-[#026857] hover:bg-[#025247] h-12 px-8 text-base">
          Request My Free Trial
        </Button>
      </section>

      {/* What You'll Experience */}
      <section className="container mx-auto px-4 py-12 max-w-5xl">
        <h2 className="text-3xl text-gray-900 text-center mb-2">What You'll Experience</h2>
        <p className="text-gray-600 text-center mb-8">
          During your complimentary trial, you'll explore:
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <Card key={title} className="p-6 bg-white">
              <Icon className="w-8 h-8 text-[#026857] mb-3" aria-hidden="true" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
              <p className="text-sm text-gray-600">{body}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Why Financial Advisors Choose bQuest */}
      <section className="container mx-auto px-4 py-12 max-w-3xl text-center">
        <h2 className="text-3xl text-gray-900 mb-6">Why Financial Advisors Choose bQuest</h2>
        <p className="text-lg text-gray-900 mb-4">
          Financial plans don't fail because of market performance alone.
        </p>
        <p className="text-gray-600 mb-4">
          They are often disrupted by aging, caregiving, cognitive decline, health events, housing
          transitions, and family dynamics.
        </p>
        <p className="text-gray-600">
          bQuest equips advisors to navigate these moments with confidence—without becoming the
          care expert. The result is stronger client relationships, deeper engagement across
          generations, and a more complete planning experience.
        </p>
      </section>

      {/* Request form */}
      <section id="trial-request-form" className="container mx-auto px-4 py-12 max-w-2xl">
        <h2 className="text-3xl text-gray-900 text-center mb-2">
          Request Your Complimentary Trial
        </h2>
        <p className="text-gray-600 text-center mb-8">
          Complete the form below to request your 7-day Advisor Trial. Once submitted, a member of
          the bQuest team will review your request and send your login information along with
          resources to help you get the most from your experience.
        </p>
        <TrialRequestForm />
      </section>

      {/* Footer taglines */}
      <section className="container mx-auto px-4 py-12 text-center">
        <p className="text-lg font-medium text-gray-900 mb-1">
          Built for Financial Advisors. Powered by Care Intelligence.
        </p>
        <p className="text-gray-600">
          One Trusted Advisor. One Care Intelligence Platform. Multigenerational Impact.
        </p>
      </section>
    </div>
  );
}
